import { useState, useMemo, useCallback, useRef } from 'react';

export interface ColumnConfig {
  title: string;
  dataIndex?: string | string[];
  key: string;
  width: number;
  [key: string]: any;
}

export const useResizableColumns = <T,>(
  baseColumns: ColumnConfig[],
  defaultWidths?: Record<string, number>
) => {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const widths: Record<string, number> = {};
    baseColumns.forEach((col) => {
      widths[col.key] = defaultWidths?.[col.key] || col.width || 150;
    });
    return widths;
  });

  // Use ref to throttle state updates
  const rafRef = useRef<number | null>(null);
  const pendingUpdateRef = useRef<Record<string, number> | null>(null);

  const handleResize = useCallback(
    (key: string) => (_: unknown, { size }: { size: { width: number } }) => {
      const newWidth = Math.max(size.width, 50); // Minimum width of 50px
      
      // Update state immediately for smooth dragging
      setColumnWidths((prev) => {
        const updated = {
          ...prev,
          [key]: newWidth,
        };
        // Store for potential batch update
        pendingUpdateRef.current = updated;
        return updated;
      });
    },
    []
  );

  const columnsWithWidths = useMemo(
    () =>
      baseColumns.map((col) => {
        const width = columnWidths[col.key] || col.width || 150;
        return {
          ...col,
          width,
          onHeaderCell: () => ({
            width,
            onResize: handleResize(col.key),
          }),
          // Explicitly preserve sorter and other properties
          sorter: col.sorter,
          render: col.render,
        };
      }),
    [baseColumns, columnWidths, handleResize]
  );

  return {
    columns: columnsWithWidths,
    columnWidths,
    setColumnWidths,
  };
};

